<template>
  <div class="x-tablelist-view quota-overview">
    <div class="search-warp">
      <el-input
        v-model="filter.name"
        clearable
        class="name-filter"
        :placeholder="$t('HT.subordinate.quota.accountPlaceholder')"
        @keyup.enter.native="search"
      >
        <i slot="prefix" class="el-input__icon el-icon-search"></i>
      </el-input>
      <el-button type="primary" size="small" @click="search">{{ $t('HT.search') }}</el-button>
      <el-button plain size="small" @click="reset">{{ $t('HT.reset') }}</el-button>
    </div>
    <div class="x-table-box">
      <ux-table
        ref="table"
        :showPage="true"
        :hide-on-single-page="true"
        maxHeight="100%"
        :data="tableData.list"
        :columns="columns"
        :total="tableData.totalSize"
        :size.sync="filter.size"
        :page.sync="filter.page"
        @changePage="changePage"
        @changeSize="changeSize"
        :emptyText="$t('HT.nodata')"
        layout="total, sizes, prev, pager, next"
      ></ux-table>
    </div>
  </div>
</template>

<script lang="jsx">
// 接口配额单位为次，页面展示单位为万次
const QUOTA_UNIT = 10000;

export default {
  name: 'QuotaOverview',
  data() {
    return {
      // 配额总览查询参数
      filter: {
        name: '',
        page: 1,
        size: 10
      },
      // 当前页账号产品配额
      accounts: [],
      // 账号产品配额记录总数
      totalSize: 0
    };
  },
  computed: {
    // ux-table 服务端分页数据和总数
    tableData() {
      return {
        totalSize: this.totalSize,
        list: this.accounts
      };
    },
    columns() {
      return [
        {
          label: this.$t('HT.subordinate.quota.accountName'),
          minWidth: 90,
          align: 'left',
          prop: 'username'
        },
        {
          label: this.$t('HT.subordinate.common.product'),
          prop: 'product_name',
          width: 170
        },
        {
          label: this.$t('HT.subordinate.common.level'),
          width: 90,
          render: (h, row) =>
            h(
              'el-tag',
              {
                props: {
                  size: 'mini',
                  type: row.owner || row.supervisor ? 'primary' : 'info'
                }
              },
              [this.getAccountLevel(row)]
            )
        },
        {
          label: this.$t('HT.subordinate.quota.allocatedQuota'),
          width: 145,
          align: 'right',
          prop: 'allocated_quota',
          formatter: (row, column, cellValue) => this.formatQuota(cellValue)
        },
        {
          label: this.$t('HT.subordinate.quota.usedQuota'),
          width: 130,
          align: 'right',
          prop: 'used_quota',
          formatter: (row, column, cellValue) => this.formatQuota(cellValue)
        },
        {
          label: this.$t('HT.subordinate.quota.remainingQuota'),
          width: 125,
          align: 'right',
          prop: 'remaining_quota',
          formatter: (row, column, cellValue) => this.formatQuota(cellValue)
        },
        {
          label: this.$t('HT.subordinate.quota.usageRate'),
          minWidth: 190,
          render: (h, row) =>
            h('div', { class: 'usage-cell' }, [
              h('el-progress', {
                props: {
                  percentage: this.getUsageRate(row),
                  strokeWidth: 8,
                  showText: false,
                  color: this.getUsageColor(row)
                }
              }),
              h('span', {}, `${this.getUsageRate(row).toFixed(1)}%`)
            ])
        },
        {
          label: this.$t('HT.orderManage.status'),
          width: 110,
          render: (h, row) =>
            h('el-tag', { props: { size: 'mini', type: this.getStatusType(row) } }, [
              this.getStatusText(row)
            ])
        },
        {
          label: this.$t('HT.subordinate.quota.allocationTime'),
          prop: 'allocation_time',
          width: 170
        },
        {
          label: this.$t('HT.operation'),
          width: 180,
          fixed: 'right',
          render: (h, row) =>
            h('div', { class: 'table-actions' }, [
              h(
                'el-button',
                { props: { type: 'text' }, on: { click: () => this.$emit('adjust', row) } },
                [this.$t('HT.subordinate.quota.adjust')]
              ),
              h(
                'el-button',
                {
                  props: { type: 'text', disabled: this.getRemaining(row) <= 0 },
                  on: { click: () => this.$emit('withdraw', row) }
                },
                [this.$t('HT.subordinate.quota.revoke')]
              )
            ])
        }
      ];
    }
  },
  created() {
    this.getList();
  },
  methods: {
    // 获取账号产品配额分页列表
    getList() {
      const params = {
        accountname: this.filter.name.trim(),
        page: this.filter.page,
        size: this.filter.size
      };
      this.$uxLoading.show();
      this.$api.accountQuota.getList(params).then((res) => {
        if (res.code == 10000) {
          this.accounts = res.data || [];
          this.totalSize = res.total_size || 0;
        }
      });
    },
    // 格式化配额数值
    formatQuota(value) {
      return (Number(value || 0) / QUOTA_UNIT).toFixed(2);
    },
    // 计算账号剩余未使用配额
    getRemaining(row) {
      return Math.max(Number(row.remaining_quota || 0), 0);
    },
    getUsageRate(row) {
      const usagePercent = Number.parseFloat(row.usage_percent);
      if (Number.isFinite(usagePercent)) {
        return Math.min(Math.max(usagePercent, 0), 100);
      }
      return row.allocated_quota
        ? Math.min(Number(row.used_quota) / Number(row.allocated_quota) * 100, 100)
        : 0;
    },
    getUsageColor(row) {
      const rate = this.getUsageRate(row);
      return rate > 95 ? '#f56c6c' : rate >= 80 ? '#e6a23c' : '#409eff';
    },
    getStatusText(row) {
      // if (row.disabled) return '禁用';
      const statusMap = {
        NORMAL: this.$t('HT.subordinate.quota.statusNormal'),
        WARNING: this.$t('HT.subordinate.quota.statusWarning'),
        EXCEEDING: this.$t('HT.subordinate.quota.statusExceeding')
      };
      return statusMap[row.status] || row.status_str || '-';
    },
    getStatusType(row) {
      if (row.status === 'EXCEEDING') return 'danger';
      return row.status === 'WARNING' ? 'warning' : 'success';
    },
    // 根据账号属性生成层级文本
    getAccountLevel(row) {
      return row.supervisor
        ? this.$t('HT.userInfo.super')
        : row.owner
          ? this.$t('HT.personal.text4')
          : this.$t('HT.personal.text5');
    },
    // 触发配额总览查询
    search() {
      this.filter.page = 1;
      this.getList();
    },
    reset() {
      this.filter = { name: '', page: 1, size: this.filter.size };
      this.getList();
    },
    changePage(page) {
      this.filter.page = page;
      this.getList();
    },
    changeSize(size) {
      this.filter.size = size;
      this.filter.page = 1;
      this.getList();
    }
  }
};
</script>

<style lang="scss" scoped>

.search-warp {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 18px;
  .name-filter {
    width: 250px;
  }
}.account-cell {
  .product-name {
    display: block;
    color: #a4adbb;
    font-size: 11px;
    line-height: 18px;
  }
}
.usage-cell {
  display: flex;
  align-items: center;
  gap: 10px;
  .el-progress {
    flex: 1;
  }
  span {
    width: 46px;
    color: #5d6879;
    font-size: 12px;
    text-align: right;
  }
}
.table-actions {
  white-space: nowrap;
}
</style>
